import React, { useEffect, useMemo } from "react";

import { LoadingOverlay } from "../../../../components/LoadingOverlay/LoadingOverlay";
import RequestsTable from "../../../../components/RequestsTable/RequestsTable";
import type { IOrganization } from "../../../../contexts/organizations/organizationsContext";
import type { IRequestsRequest } from "../../../../hooks/reactQueryHooks/useRequests";
import { useRequests } from "../../../../hooks/reactQueryHooks/useRequests";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { getRequestsInterval } from "../../../../utils/getTimeInterval";

interface RequestsPageProps {
  currentOrganization: IOrganization | undefined;
  setRequestsCount: (count: number) => void;
}

const RequestsPage: React.FC<RequestsPageProps> = ({
  currentOrganization,
  setRequestsCount,
}) => {
  const { queryValue: statusQuery } = useQueryParams({
    queryStr: "status",
  });

  const { queryValue: appQuery } = useQueryParams({
    queryStr: "projId",
  });

  const { queryValue: searchQuery } = useQueryParams({
    queryStr: "s",
  });

  const { queryValue: dateQuery, handleChange } = useQueryParams({
    queryStr: "date",
  });

  const { queryValue: pageQuery } = useQueryParams({
    queryStr: "p",
  });

  const dateFilter = useMemo(() => {
    const filterData = dateQuery?.split(",");
    const dateType = filterData?.[0];
    if (dateType !== "custom") {
      const timeInterval = getRequestsInterval(dateType);
      if (timeInterval?.fromDate) {
        return {
          fromDate: timeInterval?.fromDate,
        };
      }
      return null;
    }
    if (dateType === "custom" && filterData?.length) {
      const fromDate = new Date(Number(filterData[1]));
      const toDate = new Date(Number(filterData[2]));

      // check if the dates is valid
      if (
        isNaN(fromDate.getTime()) ||
        isNaN(toDate.getTime()) ||
        fromDate > toDate ||
        filterData.length !== 3
      ) {
        setTimeout(() => {
          handleChange("", true);
        });
        return null;
      }
      return {
        fromDate: new Date(
          new Date(fromDate.setUTCHours(0, 0, 0, 0)).setUTCDate(
            fromDate.getUTCDate() + 1,
          ),
        ),
        toDate: new Date(
          new Date(toDate.setUTCHours(23, 59, 59, 999)).setUTCDate(
            toDate.getUTCDate() + 1,
          ),
        ),
      };
    }
    return null;
  }, [dateQuery, handleChange]);

  const request: IRequestsRequest = {
    PageIndex: pageQuery ? Number(pageQuery) : 0,
    Search: searchQuery || "",
    ApplicationId: appQuery || "",
    StatusCode: statusQuery ? Number(statusQuery) : null,
    dateFilter,
  };

  const { data, isLoading, isFetching } = useRequests(
    currentOrganization?.id || "",
    request,
  );
  const { items, filteredCount: total, pageSize } = data || {};
  useEffect(() => {
    setRequestsCount(total || 0);
  }, [total, setRequestsCount]);

  return (
    <div className="card flex-shrink-1 flex-grow-1">
      <div className="app-container-inner-wrapper d-flex flex-column">
        {(isFetching || isLoading) && <LoadingOverlay />}
        <RequestsTable
          items={items || []}
          totalItems={total}
          isLoading={isLoading || isFetching}
          currentOrganization={currentOrganization}
          pageSize={pageSize || 0}
        />
      </div>
    </div>
  );
};

export { RequestsPage };
